import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

// Canvas UI's Layout scrolls this inner element rather than the window, so
// React Router's <ScrollRestoration /> (window-only) has nothing to restore.
export const SCROLL_CONTAINER_ID = 'scroll-container'
export const RESTORE_TIMEOUT_MS = 1000

const getScrollContainer = () => document.getElementById(SCROLL_CONTAINER_ID)

/**
 * Remembers the scroll position of each history entry and restores it on
 * back/forward navigation. Pushing to a new path starts at the top.
 */
export const useScrollContainerRestoration = () => {
    const location = useLocation()
    const navigationType = useNavigationType()
    const positions = useRef(new Map<string, number>())
    const currentKey = useRef(location.key)
    const previousPathname = useRef(location.pathname)

    useEffect(() => {
        const savePosition = (event: Event) => {
            if (
                event.target instanceof HTMLElement &&
                event.target.id === SCROLL_CONTAINER_ID
            ) {
                positions.current.set(
                    currentKey.current,
                    event.target.scrollTop,
                )
            }
        }
        document.addEventListener('scroll', savePosition, true)
        return () => document.removeEventListener('scroll', savePosition, true)
    }, [])

    useLayoutEffect(() => {
        const previousKey = currentKey.current
        const pathnameChanged = previousPathname.current !== location.pathname
        currentKey.current = location.key
        previousPathname.current = location.pathname

        if (navigationType === 'REPLACE') {
            // Replacing (e.g. switching a time period) creates a new history key for
            // the same screen, so carry the position over or "back" would lose it.
            const saved = positions.current.get(previousKey)
            if (saved !== undefined) positions.current.set(location.key, saved)
            return
        }

        if (navigationType === 'PUSH') {
            const container = getScrollContainer()
            if (pathnameChanged && container) container.scrollTop = 0
            return
        }

        const target = positions.current.get(location.key)
        if (target === undefined) return

        // Cached content may take a few frames to reach full height, so retry briefly,
        // but stop as soon as the user starts scrolling so we never fight them.
        let frame = 0
        let cancelled = false
        const cancel = () => {
            cancelled = true
            cancelAnimationFrame(frame)
        }
        const startedAt = performance.now()
        const restore = () => {
            if (cancelled) return
            const container = getScrollContainer()
            if (container) {
                container.scrollTop = target
                if (Math.abs(container.scrollTop - target) <= 1) return
            }
            if (performance.now() - startedAt < RESTORE_TIMEOUT_MS) {
                frame = requestAnimationFrame(restore)
            }
        }
        const userInputEvents = ['wheel', 'touchstart', 'keydown'] as const
        userInputEvents.forEach((name) =>
            window.addEventListener(name, cancel, {
                passive: true,
                once: true,
            }),
        )
        restore()
        return () => {
            cancel()
            userInputEvents.forEach((name) =>
                window.removeEventListener(name, cancel),
            )
        }
    }, [location.key, location.pathname, navigationType])
}
